import React, { useCallback, useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ChatBox from "@/components/ChatBox";
import ShareButton from "@/components/ShareButton";
import { useSEO } from "@/hooks/useSEO";
import { scrollToPageTop } from "@/lib/scroll";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthDialog } from "@/components/AuthDialog";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ExternalLink, MessageCircle, Car, Plus, Sparkles, MapPin, ShoppingBag, CalendarHeart, Star, Lock, Loader2, Send } from "lucide-react";

const PARTNERS = [
  "elTimonPopoyo.com",
  "VillaTranquilaPopoyo.com",
  "CasaElioPopoyo.com",
  "CasaRomanoPopoyo.com",
  "JahoskaChamorro.com",
  "DalePuesPopoyo.com",
  "NicaMotoRentalsPopoyo.com",
  "CascanteMotoRental.com",
  "JhossyMotoRentalPopoyo.com",
  "HangZenSurfSea.com",
  "NicaBros.com",
  "HogTideFishing.com",
  "DrCoraNicaragua.com",
  "SOSDeliPopoyo.com",
  "PizzaHousePopoyo.com",
  "MiamiVibesPopoyo.com",
  "CasaDeVistaRentals.com",
  "SurFishToursPopoyo.com",
];

const thumbFor = (d) => `https://s.wordpress.com/mshots/v1/${encodeURIComponent("https://www." + d)}?w=720`;

const COMING_SOON_TILES = [
  { key: "locals_cs_directory", icon: MapPin, accent: "#1B3B6F" },
  { key: "locals_cs_buy_sell", icon: ShoppingBag, accent: "#E76F51" },
  { key: "locals_cs_events", icon: CalendarHeart, accent: "#2A9D8F" },
  { key: "locals_cs_features", icon: Star, accent: "#F4A261" },
];

function SectionHeader({ eyebrow, title, subtitle, icon: Icon, accent }) {
  return (
    <div className="flex items-start gap-3 mb-3">
      <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: accent }}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1">
        <p className="eyebrow">{eyebrow}</p>
        <h2 className="text-2xl sm:text-3xl font-bold leading-tight" style={{ fontFamily: "Outfit" }}>{title}</h2>
        {subtitle && <p className="text-sm text-[color:var(--ink-2)] mt-1 max-w-2xl">{subtitle}</p>}
      </div>
    </div>
  );
}

function ComingSoonChip() {
  const { t } = useI18n();
  return (
    <span className="inline-flex items-center gap-1 pill px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-[color:var(--coral)] text-white">
      {t("locals_coming_soon")}
    </span>
  );
}

export default function PopoyoLocals() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { open: openAuth } = useAuthDialog();
  const signedIn = !!user?.id;

  React.useEffect(() => { scrollToPageTop(); }, []);

  // Approved community-submitted businesses (rendered under the founder list)
  const [community, setCommunity] = useState([]);
  const loadCommunity = useCallback(async () => {
    try {
      const { data } = await http.get("/local-businesses", { params: { limit: 200 } });
      setCommunity(Array.isArray(data) ? data : []);
    } catch { /* silent — public feed */ }
  }, []);
  useEffect(() => { loadCommunity(); }, [loadCommunity]);

  // Add-your-website form state
  const [showForm, setShowForm] = useState(false);
  const [fDomain, setFDomain] = useState("");
  const [fName, setFName] = useState("");
  const [fCategory, setFCategory] = useState("");
  const [fDesc, setFDesc] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submitBusiness = async (e) => {
    e.preventDefault();
    if (!signedIn) { openAuth("register"); return; }
    if (!fDomain.trim()) { toast.error("Enter your website domain"); return; }
    setSubmitting(true);
    try {
      await http.post("/local-businesses/submissions", {
        domain: fDomain.trim(),
        name: fName.trim() || null,
        category: fCategory.trim() || null,
        description: fDesc.trim() || null,
      });
      toast.success("Thanks — your website is queued for review!");
      setFDomain(""); setFName(""); setFCategory(""); setFDesc("");
      setShowForm(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  useSEO({
    title: "Popoyo Local Businesses Directory · Popoyo.app",
    description:
      "The official directory of Popoyo, Nicaragua local businesses — hotels, rentals, surf schools, restaurants, moto rentals, medical services and more. Support the community that makes Popoyo home.",
    path: "/popoyo-locals",
  });

  return (
    <div className="min-h-screen flex flex-col" data-testid="popoyo-locals-page">
      <Header />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

        {/* Hero */}
        <section className="clay p-6 md:p-8 text-center">
          <p className="eyebrow text-[color:var(--coral)]">{t("locals_hero_eyebrow")}</p>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight mt-1" style={{ fontFamily: "Outfit" }}>
            {t("locals_hero_title")}
          </h1>
          <p className="text-base text-[color:var(--ink-2)] mt-2 max-w-2xl mx-auto">{t("locals_hero_subtitle")}</p>
          <div className="mt-4 flex justify-center">
            <ShareButton
              path="/popoyo-locals"
              title="Popoyo Local Businesses Directory · Popoyo.app"
              text="The official Popoyo, Nicaragua business directory — hotels, rentals, surf, restaurants and more."
              testId="share-popoyo-locals-btn"
            />
          </div>
        </section>

        {/* Community chat */}
        <section data-testid="locals-chat" className="clay p-5 md:p-6">
          <SectionHeader
            eyebrow={t("locals_chat_eyebrow")}
            title={t("locals_chat_title")}
            subtitle={t("locals_chat_subtitle")}
            icon={MessageCircle}
            accent="#2A9D8F"
          />
          <ChatBox onOpenAuth={() => openAuth("login")} />
        </section>

        {/* Local businesses */}
        <section data-testid="locals-businesses" className="clay p-5 md:p-6">
          <div className="flex items-start gap-3 mb-3 flex-wrap">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "var(--teal)" }}>
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <p className="eyebrow">{t("locals_biz_eyebrow")}</p>
              <h2 className="text-2xl sm:text-3xl font-bold leading-tight" style={{ fontFamily: "Outfit" }}>
                {t("locals_biz_title")}
              </h2>
              <p className="text-sm text-[color:var(--ink-2)] mt-1">{t("locals_biz_subtitle")}</p>
            </div>
            {signedIn ? (
              <button
                onClick={() => setShowForm((v) => !v)}
                data-testid="locals-add-business-cta"
                className="pill px-4 py-2 text-sm font-bold bg-[color:var(--navy)] text-white hover:bg-[color:var(--navy-2)] inline-flex items-center gap-1.5"
              >
                <Plus className="w-4 h-4" /> {t("locals_biz_add")}
              </button>
            ) : (
              <button
                onClick={() => openAuth("register")}
                data-testid="locals-add-business-signin"
                className="pill px-4 py-2 text-sm font-bold bg-[color:var(--coral)] text-white hover:opacity-90 inline-flex items-center gap-1.5"
              >
                <Lock className="w-4 h-4" /> {t("locals_biz_signin_to_add")}
              </button>
            )}
          </div>

          {/* Add-your-website submission form (auth-only, admin-approved) */}
          {signedIn && showForm && (
            <form
              onSubmit={submitBusiness}
              data-testid="locals-business-form"
              className="rounded-2xl border border-[color:var(--teal)]/25 bg-[color:var(--sand-2)]/60 p-4 mt-3 grid gap-2"
            >
              <p className="text-[11px] uppercase tracking-widest font-bold text-[color:var(--teal)]">
                Add your Popoyo website — reviewed by our team before it goes public
              </p>
              <div className="grid sm:grid-cols-[1fr_180px] gap-2">
                <Input
                  data-testid="locals-form-domain"
                  value={fDomain}
                  onChange={(e) => setFDomain(e.target.value)}
                  placeholder="YourBusinessPopoyo.com"
                  maxLength={253}
                  className="rounded-2xl bg-white"
                  required
                />
                <Input
                  data-testid="locals-form-category"
                  value={fCategory}
                  onChange={(e) => setFCategory(e.target.value)}
                  placeholder="Category (rental, food, surf…)"
                  maxLength={40}
                  className="rounded-2xl bg-white"
                />
              </div>
              <Input
                data-testid="locals-form-name"
                value={fName}
                onChange={(e) => setFName(e.target.value)}
                placeholder="Business name (optional — defaults to domain)"
                maxLength={140}
                className="rounded-2xl bg-white"
              />
              <Input
                data-testid="locals-form-desc"
                value={fDesc}
                onChange={(e) => setFDesc(e.target.value)}
                placeholder="One-sentence description (optional, 400 char max)"
                maxLength={400}
                className="rounded-2xl bg-white"
              />
              <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
                <span className="text-[11px] text-[color:var(--ink-2)]">
                  Signed in as <strong>{user.name || user.username}</strong>
                </span>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowForm(false)}
                    className="pill"
                    data-testid="locals-form-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="locals-form-submit"
                    className="pill bg-[color:var(--teal)] text-white hover:opacity-90"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                    Submit for review
                  </Button>
                </div>
              </div>
            </form>
          )}

          {/* Featured promo: NicaBros × El Timón game — win $25 gift card + 10% off */}
          <a
            href="https://nicabros.com/eltimon.html?v=2"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="locals-eltimon-promo"
            className="mt-4 block rounded-2xl overflow-hidden border-2 border-[color:var(--coral)] shadow-[0_10px_28px_-14px_rgba(231,111,81,0.55)] hover:shadow-[0_16px_32px_-14px_rgba(231,111,81,0.7)] transition-all hover:-translate-y-0.5"
            style={{ background: "linear-gradient(135deg, #1B3B6F 0%, #0F2340 100%)" }}
          >
            <div className="grid sm:grid-cols-[auto_1fr_auto] gap-4 p-4 sm:p-5 items-center">
              <div
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center shrink-0"
                style={{ background: "var(--coral)" }}
              >
                <Sparkles className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
              <div className="text-white">
                <p className="text-[10px] sm:text-[11px] uppercase tracking-widest font-bold text-[color:var(--coral)]">
                  NicaBros × El Timón · Special Level
                </p>
                <h3 className="text-lg sm:text-xl font-bold leading-tight mt-0.5" style={{ fontFamily: "Outfit" }}>
                  Win a $25 El Timón gift card + 10% off your next bill
                </h3>
                <p className="text-xs sm:text-sm text-white/80 mt-1">
                  Play the special <strong>NicaBros El Timón</strong> level to unlock <strong>10% off your next bill</strong> at El Timón. Top monthly score wins a <strong>$25 gift card</strong>.
                </p>
              </div>
              <span
                className="pill inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-[color:var(--navy)] whitespace-nowrap"
                style={{ background: "#F4C542" }}
              >
                Play & win <ExternalLink className="w-4 h-4" />
              </span>
            </div>
          </a>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-3">
            {PARTNERS.map((domain) => {
              const len = domain.length;
              const fontPx = len >= 22 ? 10 : len >= 20 ? 11 : 12;
              return (
                <a
                  key={domain}
                  href={`https://www.${domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`locals-partner-${domain}`}
                  className="group block rounded-2xl overflow-hidden bg-white border border-[color:var(--ink-2)]/10 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all"
                >
                  <div className="aspect-[16/10] w-full bg-[color:var(--sand-2)] overflow-hidden">
                    <img src={thumbFor(domain)} alt={`${domain} preview`} loading="lazy" className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform" />
                  </div>
                  <div className="p-2 flex flex-col items-stretch gap-1.5">
                    <p
                      className="font-semibold text-[color:var(--ink)] leading-tight text-center whitespace-nowrap"
                      style={{ fontSize: `${fontPx}px`, letterSpacing: "-0.01em" }}
                    >
                      {domain}
                    </p>
                    <span className="inline-flex items-center justify-center gap-1 pill px-2.5 py-1 text-[11px] font-bold bg-[color:var(--navy)] text-white group-hover:bg-[color:var(--coral)]">
                      <ExternalLink className="w-3 h-3" /> Visit
                    </span>
                  </div>
                </a>
              );
            })}
          </div>

          {/* Community-submitted businesses (admin-approved) */}
          {community.length > 0 && (
            <div className="mt-6">
              <p className="eyebrow text-[color:var(--coral)] mb-2">
                Submitted by the community
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="locals-community-grid">
                {community.map((biz) => {
                  const domain = biz.domain;
                  const len = domain.length;
                  const fontPx = len >= 22 ? 10 : len >= 20 ? 11 : 12;
                  return (
                    <a
                      key={biz.id}
                      href={`https://www.${domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`locals-community-${domain}`}
                      className="group block rounded-2xl overflow-hidden bg-white border border-[color:var(--teal)]/25 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all"
                      title={biz.description || `www.${domain}`}
                    >
                      <div className="aspect-[16/10] w-full bg-[color:var(--sand-2)] overflow-hidden">
                        <img src={thumbFor(domain)} alt={`${biz.name} preview`} loading="lazy" className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform" />
                      </div>
                      <div className="p-2 flex flex-col items-stretch gap-1.5">
                        <p
                          className="font-semibold text-[color:var(--ink)] leading-tight text-center whitespace-nowrap"
                          style={{ fontSize: `${fontPx}px`, letterSpacing: "-0.01em" }}
                        >
                          {domain}
                        </p>
                        <span className="inline-flex items-center justify-center gap-1 pill px-2.5 py-1 text-[11px] font-bold bg-[color:var(--teal)] text-white group-hover:bg-[color:var(--coral)]">
                          <ExternalLink className="w-3 h-3" /> Visit
                        </span>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {/* Nicaragua Taxi */}
        <section data-testid="locals-taxi" className="clay p-5 md:p-6">
          <SectionHeader
            eyebrow={t("locals_taxi_eyebrow")}
            title={t("locals_taxi_title")}
            subtitle={t("locals_taxi_subtitle")}
            icon={Car}
            accent="#F4A261"
          />
          <div className="rounded-2xl overflow-hidden grid sm:grid-cols-[1fr_220px] gap-4 items-center" style={{ background: "linear-gradient(135deg, #0F2340 0%, #0A1628 65%, #060D1A 100%)", color: "#fff" }}>
            <div className="p-5 sm:p-6">
              <p className="text-[11px] uppercase tracking-widest opacity-80">Nicaragua Taxi Services</p>
              <p className="text-lg sm:text-xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>{t("nicaragua_taxi_tagline")}</p>
              <a
                href="https://www.NicaraguaTaxi.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="locals-taxi-link"
                className="mt-3 inline-flex items-center gap-2 pill px-5 py-2.5 text-sm font-bold text-[#0A1628] hover:scale-[1.03] transition-transform"
                style={{ background: "#FFC700", boxShadow: "0 8px 24px rgba(255,199,0,0.35)" }}
              >
                <ExternalLink className="w-4 h-4" />
                www.NicaraguaTaxi.com
              </a>
            </div>
            <div className="hidden sm:block p-4">
              <img src="/brand/nicaragua-taxi-logo.png" alt="Nicaragua Taxi Services" className="w-full max-w-[200px] mx-auto" loading="lazy" />
            </div>
          </div>
        </section>

        {/* Coming soon tiles */}
        <section data-testid="locals-coming-soon" className="clay p-5 md:p-6">
          <SectionHeader
            eyebrow={t("locals_cs_eyebrow")}
            title={t("locals_cs_title")}
            subtitle={t("locals_cs_subtitle")}
            icon={Sparkles}
            accent="#E76F51"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {COMING_SOON_TILES.map(({ key, icon: Icon, accent }) => (
              <div
                key={key}
                data-testid={`locals-cs-${key}`}
                className="rounded-2xl p-4 border border-[color:var(--ink-2)]/10 bg-white hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: accent }}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <ComingSoonChip />
                </div>
                <p className="font-bold text-sm leading-tight" style={{ fontFamily: "Outfit" }}>{t(`${key}_title`)}</p>
                <p className="text-xs text-[color:var(--ink-2)] mt-1 leading-snug">{t(`${key}_desc`)}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 text-center">
            <a
              href="/#suggestions"
              data-testid="locals-suggest-cta"
              className="inline-flex items-center gap-1.5 pill px-4 py-2 text-sm font-bold bg-[color:var(--coral)] text-white hover:opacity-90"
            >
              <Plus className="w-4 h-4" /> {t("locals_suggest_idea")}
            </a>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
